import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function UserContact() {
	const { t } = useTranslation();
	const { mutate } = trpc.user.contact.useMutation();

	const formSchema = z.object({
		email: z.string().email(t('contact.error.email')),
		message: z.string().min(10, t('contact.error.message')),
	});

	type FormData = z.infer<typeof formSchema>;

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(formSchema),
	});

	const onSubmit = async (data: FormData) => {
		mutate({ message: data.message, mail: data.email });
		reset();
		toast.success('Message sent');
	};

	return (
		<Card className="mx-auto w-full max-w-md">
			<CardHeader>
				<CardTitle>{t('contact.title')}</CardTitle>
				<CardDescription>{t('contact.description')}</CardDescription>
			</CardHeader>
			<form onSubmit={handleSubmit(onSubmit)}>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="email">Email</Label>
						<Input
							id="email"
							type="email"
							{...register('email')}
							aria-invalid={errors.email ? 'true' : 'false'}
						/>
						{errors.email && (
							<p className="flex items-center gap-1 text-sm text-red-500">
								<AlertCircle className="h-4 w-4" />
								{errors.email.message}
							</p>
						)}
					</div>
					<div className="space-y-2">
						<Label htmlFor="message">Message</Label>
						<Textarea
							id="message"
							{...register('message')}
							aria-invalid={errors.message ? 'true' : 'false'}
						/>
						{errors.message && (
							<p className="flex items-center gap-1 text-sm text-red-500">
								<AlertCircle className="h-4 w-4" />
								{errors.message.message}
							</p>
						)}
					</div>
				</CardContent>
				<CardFooter>
					<Button type="submit" className="w-full">
						{t('contact.submit')}
					</Button>
				</CardFooter>
			</form>
		</Card>
	);
}
